import { type ReactElement } from 'react';
import { redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { type MoneyDocumentPage } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { DocumentList } from '../features/billing/documents/DocumentList.js';
import { documentsPath } from '../features/billing/documents/documents.js';
import { type DocumentLocale } from '../features/billing/documents/rows.js';
import { ApiError } from '../shared/api.js';
import { routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `/:lang/account/documents` — the caller's provisional receipts and payout statements, newest
 * first, `?cursor=` for the next page. `W5-T09` §4.9, AC 35; owned by `agent-money`.
 */
interface DocumentsData {
  locale: DocumentLocale;
  page: MoneyDocumentPage;
}

export async function loader({
  params,
  request,
  context,
}: LoaderFunctionArgs): Promise<DocumentsData> {
  const locale = localeOf(params) === 'en' ? 'en' : 'es';
  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const cursor = new URL(request.url).searchParams.get('cursor') ?? undefined;
  const { api } = context.get(routeContext);
  try {
    // Not cached: a document issued a minute ago is what the reader came for.
    return { locale, page: await api.getMyDocuments({ cursor }) };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) throw redirect(`/${locale}/login`);
    throw error;
  }
}

export function Component(): ReactElement {
  const { locale, page } = useLoaderData<DocumentsData>();
  const next = page.page.nextCursor;
  return (
    <DocumentList
      items={page.items}
      locale={locale}
      nextHref={
        next === null ? null : `${documentsPath(locale)}?cursor=${encodeURIComponent(next)}`
      }
    />
  );
}
